import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getPipeline, listPipelines } from '@/api/pipelines'
import type { Deal, PipelineSummary } from '@/api/types'
import {
  addDealContact,
  boardDeals,
  changeDealContactRole,
  createDeal,
  deleteDeal,
  getDeal,
  listDealContacts,
  listDeals,
  moveDeal,
  removeDealContact,
  setDealPrimaryContact,
  updateDeal,
  type AddDealContactParams,
  type ChangeDealContactRoleParams,
  type CreateDealParams,
  type MoveFills,
  type UpdateDealParams,
} from '@/api/deals'

export function usePipelines() {
  return useQuery({ queryKey: ['pipelines'], queryFn: listPipelines })
}

const PIPELINE_STORAGE_KEY = 'pp.selectedPipelineId'

/** Remembers the board's pipeline choice across visits; falls back to default → first. */
export function useSelectedPipeline(pipelines: PipelineSummary[] | undefined) {
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    localStorage.getItem(PIPELINE_STORAGE_KEY),
  )
  const pipeline =
    pipelines?.find((p) => p.id === selectedId) ??
    pipelines?.find((p) => p.isDefault) ??
    pipelines?.[0]
  const select = useCallback((id: string) => {
    localStorage.setItem(PIPELINE_STORAGE_KEY, id)
    setSelectedId(id)
  }, [])
  return { pipeline, select }
}

export function usePipelineDetail(pipelineId?: string) {
  return useQuery({
    queryKey: ['pipeline', pipelineId],
    queryFn: () => getPipeline(pipelineId as string),
    enabled: !!pipelineId,
  })
}

export function useBoardDeals(pipelineId?: string) {
  return useQuery({
    queryKey: ['deals', 'board', pipelineId],
    queryFn: () => boardDeals(pipelineId as string),
    enabled: !!pipelineId,
  })
}

export function useMoveDeal(pipelineId?: string) {
  const qc = useQueryClient()
  const boardKey = ['deals', 'board', pipelineId]
  return useMutation({
    mutationFn: ({ dealId, stageId, fills }: { dealId: string; stageId: string; fills?: MoveFills }) =>
      moveDeal(dealId, stageId, fills),
    // Optimistic: move the card in the cache immediately (prod RTT makes a
    // refetch-first approach visibly laggy), roll back on error, then reconcile.
    onMutate: async ({ dealId, stageId }) => {
      await qc.cancelQueries({ queryKey: boardKey })
      const previous = qc.getQueryData<Deal[]>(boardKey)
      qc.setQueryData<Deal[]>(boardKey, (deals) =>
        deals?.map((d) => (d.id === dealId ? { ...d, stageId } : d)),
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(boardKey, context.previous)
    },
    onSettled: (_data, _err, { dealId }) => {
      qc.invalidateQueries({ queryKey: boardKey })
      // The deal's own record (its stage, status when moved to won/lost, and any required fields filled
      // with the move) and its timeline's "stage change" entry — the move can be made from the deal itself.
      qc.invalidateQueries({ queryKey: ['deal', dealId] })
      qc.invalidateQueries({ queryKey: ['activities', dealId] })
      qc.invalidateQueries({ queryKey: ['attention'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      // A linked contact's Deals list shows the deal's stage name — refresh after a move.
      qc.invalidateQueries({ queryKey: ['contact-deals'] })
    },
  })
}

export function useCreateDeal(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: CreateDealParams) => createDeal(params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'board', pipelineId] })
      qc.invalidateQueries({ queryKey: ['attention'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['deals', 'list'] }) // the "link to deal" picker
      qc.invalidateQueries({ queryKey: ['contact-deals'] }) // created with a client → their Deals list
    },
  })
}

export function useDeal(dealId?: string) {
  return useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => getDeal(dealId as string),
    enabled: !!dealId,
  })
}

export function useUpdateDeal(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateDealParams }) => updateDeal(id, params),
    onSuccess: (deal) => {
      // Push the saved copy into the detail cache synchronously so the open panel reflects it at once
      // (no refetch flicker); the invalidations below refresh the board/lists/aggregates.
      qc.setQueryData(['deal', deal.id], deal)
      qc.invalidateQueries({ queryKey: ['deals', 'board', pipelineId] })
      qc.invalidateQueries({ queryKey: ['deal', deal.id] })
      qc.invalidateQueries({ queryKey: ['attention'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      // A linked contact's Deals list shows the deal title/stage; the picker shows its title.
      qc.invalidateQueries({ queryKey: ['contact-deals'] })
      qc.invalidateQueries({ queryKey: ['deals', 'list'] })
    },
  })
}

export function useDeleteDeal(pipelineId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteDeal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'board', pipelineId] })
      qc.invalidateQueries({ queryKey: ['attention'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      // Remove the deal from any linked contact's Deals list and the picker.
      qc.invalidateQueries({ queryKey: ['contact-deals'] })
      qc.invalidateQueries({ queryKey: ['deals', 'list'] })
    },
  })
}

export function useDealContacts(dealId?: string) {
  return useQuery({
    queryKey: ['deal-contacts', dealId],
    queryFn: () => listDealContacts(dealId as string),
    enabled: !!dealId,
  })
}

export function useAddDealContact(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: AddDealContactParams) => addDealContact(dealId, params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal-contacts', dealId] })
      qc.invalidateQueries({ queryKey: ['contact-deals'] }) // the contact side of the link
      qc.invalidateQueries({ queryKey: ['deal', dealId] }) // first link auto-sets the primary
    },
  })
}

/**
 * The agency's deals, for the contact card's "Link to deal" picker (client-filtered by title).
 * `enabled` lets callers defer the fetch until the picker is actually opened.
 */
export function useDeals({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: ['deals', 'list'], queryFn: () => listDeals(), enabled })
}

/**
 * Link a deal to a contact from the contact side. Same endpoint as {@link useAddDealContact}
 * (POST /api/deals/{dealId}/contacts), but the deal is chosen per-call rather than fixed at
 * the hook — so one card can attach the contact to any picked deal.
 */
export function useLinkDealToContact(contactId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealId, role }: { dealId: string; role: string }) =>
      addDealContact(dealId, { contactId, role }),
    onSuccess: (_data, { dealId }) => {
      qc.invalidateQueries({ queryKey: ['contact-deals', contactId] })
      qc.invalidateQueries({ queryKey: ['deal', dealId] }) // first link auto-sets the primary
      qc.invalidateQueries({ queryKey: ['deal-contacts', dealId] })
    },
  })
}

/** Change the role a linked contact plays on the deal, without unlinking it. */
export function useChangeDealContactRole(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: ChangeDealContactRoleParams) => changeDealContactRole(dealId, params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal-contacts', dealId] })
      qc.invalidateQueries({ queryKey: ['contact-deals'] }) // the contact side shows the role too
    },
  })
}

export function useRemoveDealContact(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) => removeDealContact(dealId, contactId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal-contacts', dealId] })
      qc.invalidateQueries({ queryKey: ['contact-deals'] })
      qc.invalidateQueries({ queryKey: ['deal', dealId] }) // detaching may reassign the primary
    },
  })
}

/** Promote an already-linked contact to the deal's primary (the send / AI-draft recipient). */
export function useSetDealPrimaryContact(dealId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contactId: string) => setDealPrimaryContact(dealId, contactId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal', dealId] })
      qc.invalidateQueries({ queryKey: ['deal-contacts', dealId] })
    },
  })
}
